import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../../context/AuthContext';
import {
  partsHeureReunion,
  todayISOReunion,
  nowMinutesReunion,
  formatHeureReunion,
  reunionWallClockToDate,
} from '../../utils/reunionTime';
import { 
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  PhoneIcon,
  EnvelopeIcon,
  QuestionMarkCircleIcon,
  ClockIcon,
  CalendarDaysIcon,
  XMarkIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY_AMAIR);

const TACHES = [
  {
    value: 'depot',
    label: 'Dépôt de hausses',
    needsQuantity: true,
    unit: 'hausses déposées',
    unitSingular: 'hausse déposée',
    max: 1,
    portee: 'jour',
  },
  {
    value: 'cire',
    label: 'Gaufrage de cire',
    needsQuantity: true,
    unit: 'kg de cire à gaufrer (feuille alvéolée imprimée)',
    unitSingular: 'kg de cire à gaufrer (feuille alvéolée imprimée)',
    max: 1,
    portee: 'jour',
  },
  {
    value: 'conditionnement',
    label: 'Conditionnement de miel',
    needsQuantity: true,
    unit: 'kg de miel à conditionner',
    unitSingular: 'kg de miel à conditionner',
    max: 1,
    portee: 'jour',
    aide: 'Une réservation par jour. Saisissez le poids prévu en kg : le tarif s’applique par tranche de 5 kg.',
  },
  {
    value: 'candi',
    label: 'Fabrication de candi',
    needsQuantity: true,
    unit: 'sacs de sucre (25 kg) à moudre',
    unitSingular: 'sac de sucre (25 kg) à moudre',
    max: 3,
    portee: 'slot',
  },
  {
    value: 'extraction_hausses',
    label: 'Extraction des hausses',
    needsQuantity: true,
    unit: 'hausses prévues à extraire',
    unitSingular: 'hausse prévue à extraire',
    max: 1,
    portee: 'jour',
  },
];

function jourISO(plage) {
  const j = plage?.jour ?? plage?.date;
  if (!j) return '';
  if (typeof j === 'string') return j.split('T')[0];
  try {
    return new Date(j).toISOString().split('T')[0];
  } catch {
    return '';
  }
}

function dateLocalePlage(p) {
  const iso = jourISO(p);
  if (!iso) return '';
  return new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR');
}

function debutPlage(plage) {
  return plage?.heureOuvertureDebut ?? plage?.heureDebut ?? '';
}

function finPlage(plage) {
  return plage?.heureOuvertureFin ?? plage?.heureFin ?? '';
}

function prestationsProposeesPlage(plage) {
  return plage?.prestationsProposees ?? plage?.servicesOuverts;
}

/** Plage d'ouverture sans liste prestations = rétrocompat, toutes les tâches autorisées */
function tachesDisponiblesPourSlot(slot) {
  const actives = TACHES.filter((t) => t.disponible !== false);
  const ouverts = prestationsProposeesPlage(slot);
  if (!Array.isArray(ouverts) || ouverts.length === 0) return actives;
  const set = new Set(ouverts.map((v) => (v === 'extraction' ? 'extraction_hausses' : v)));
  return actives.filter((t) => set.has(t.value));
}

function estReservationAnnuleeOuAbsente(r) {
  return r && (r.statut === 'annulee' || r.statut === 'absente');
}

function mesReservationsActivesPourSlot(mesReservations, plageId) {
  if (!plageId) return [];
  const list = mesReservations.filter((r) => {
    if (estReservationAnnuleeOuAbsente(r)) return false;
    const p = r.plageOuverture || r.slot;
    return p && String(p._id || p) === String(plageId);
  });
  return [...list].sort(comparerReservationsParHoraireArrivee);
}

/** Plages du jour : créneaux ouverts + plages de mes réservations (ex. si la liste ouverte ne les contient pas). */
function plagesJourAvecReservationsUtilisateur(dayDate, slotsOuverts, mesReservations) {
  const key = dayDate.toDateString();
  const byId = new Map();
  for (const slot of slotsOuverts) {
    const jd = jourISO(slot);
    if (!jd) continue;
    if (new Date(`${jd}T12:00:00`).toDateString() !== key) continue;
    const id = idPlageOuverture(slot);
    if (id) byId.set(String(id), slot);
  }
  for (const r of mesReservations) {
    if (estReservationAnnuleeOuAbsente(r)) continue;
    const plage = plageDepuisReservation(r);
    if (!plage) continue;
    const jd = jourISO(plage);
    if (!jd) continue;
    if (new Date(`${jd}T12:00:00`).toDateString() !== key) continue;
    const id = idPlageOuverture(plage);
    if (!id) continue;
    if (!byId.has(String(id))) byId.set(String(id), plage);
  }
  return [...byId.values()].sort((a, b) => debutPlage(a).localeCompare(debutPlage(b)));
}

function libelleTacheType(type) {
  if (type === 'extraction') return 'Extraction des hausses';
  return TACHES.find((t) => t.value === type)?.label || type;
}

function typesPrestationEquivalents(type) {
  if (type === 'extraction' || type === 'extraction_hausses') {
    return ['extraction', 'extraction_hausses'];
  }
  return [type];
}

function reservationInclutPrestation(types, typeValue) {
  const equiv = new Set(typesPrestationEquivalents(typeValue));
  return (types || []).some((tp) => equiv.has(tp));
}

/** Accorde l’unité selon la quantité (ex. 1 hausse déposée / 2 hausses déposées). */
function libelleQuantiteUnite(quantite, tacheInfo) {
  if (!tacheInfo?.unit) return String(quantite ?? '');
  const q = Number(quantite);
  const unit =
    q === 1 && tacheInfo.unitSingular ? tacheInfo.unitSingular : tacheInfo.unit;
  return `${Number.isFinite(q) ? q : quantite} ${unit}`;
}

function normaliserOrigineRucher(tache = {}) {
  if (['mon', 'sar', 'autre'].includes(tache.origineRucher)) {
    return {
      origineRucher: tache.origineRucher,
      autreRucherPrecision:
        tache.origineRucher === 'autre'
          ? String(tache.autreRucherPrecision || '').trim()
          : '',
    };
  }
  return {
    origineRucher: tache.pourAutreRucher ? 'autre' : 'mon',
    autreRucherPrecision: String(tache.autreRucherPrecision || '').trim(),
  };
}

function libelleOrigineRucher(tache = {}) {
  const { origineRucher, autreRucherPrecision } = normaliserOrigineRucher(tache);
  if (origineRucher === 'sar') return 'Un rucher géré par le SAR';
  if (origineRucher === 'autre') {
    return autreRucherPrecision
      ? `Autre rucher : ${autreRucherPrecision}`
      : 'Autre rucher';
  }
  return 'Mon rucher';
}

function estDettePartielle(r) {
  const p = r?.paiement;
  if (p?.statut !== 'dette') return false;
  const total = Math.round(Number(r?.montantTotal || 0) * 100) / 100;
  const prepaye = Math.round(Number(r?.montantPrepaye || 0) * 100) / 100;
  const aUnReste = p.montantRestantDette != null && p.montantRestantDette !== '';
  const restant = aUnReste
    ? Math.round(Number(p.montantRestantDette) * 100) / 100
    : Math.round(Math.max(0, total - prepaye) * 100) / 100;
  const dejaPaye = Math.round(Math.max(0, total - restant) * 100) / 100;
  return restant > 0 && dejaPaye > 0;
}

function libelleModePaiementRealise(r) {
  const p = r?.paiement;
  if (!p) return '';
  if (p.statut === 'dette') {
    return estDettePartielle(r) ? 'en dette · partiellement payé' : 'en dette';
  }
  const methodes = {
    cheque: 'chèque',
    espece: 'espèces',
    stripe: 'en ligne',
    solde: 'avoir',
    virement: 'virement',
  };
  if (p.methode && methodes[p.methode]) return methodes[p.methode];
  if (p.statut === 'paye') return 'réglé';
  return '';
}

function libelleStatutReservation(statut, reservation) {
  const labels = {
    reservee: 'Réservée',
    honoree: 'Réalisée',
    annulee: 'Annulée',
    absente: 'Absent·e',
  };
  const base = labels[statut] || statut;
  if (statut !== 'honoree') return base;
  const mode = libelleModePaiementRealise(reservation);
  return mode ? `${base} · ${mode}` : base;
}

/** Objet plage peuplé sur une réservation ; si `slot` n’est qu’un id (string), retourne `null`. */
function plageDepuisReservation(reservation) {
  if (!reservation) return null;
  const p = reservation.plageOuverture || reservation.slot;
  if (p == null || typeof p !== 'object') return null;
  return p;
}

/** Aligné sur le backend (`peutEtreAnnulee`) : statut « réservée », jour de plage ≥ aujourd’hui, et plus de 48 h avant la plage. */
function reservationPeutEtreAnnuleeParUsager(reservation) {
  if (!reservation || reservation.statut !== 'reservee') return false;
  const plage = plageDepuisReservation(reservation);
  if (!plage) return false;
  const iso = jourISO(plage);
  if (!iso) return false;
  const jour = iso.split('T')[0];
  if (jour < todayISOReunion()) return false;
  const start = reunionWallClockToDate(jour, debutPlage(plage) || '00:00');
  if (!start) return false;
  const heuresAvant = (start.getTime() - Date.now()) / (1000 * 60 * 60);
  return heuresAvant > 48;
}

function reservationEstActiveEtAVenir(reservation) {
  if (!reservation || estReservationAnnuleeOuAbsente(reservation)) return false;
  const plage = plageDepuisReservation(reservation);
  if (!plage) return false;
  const iso = jourISO(plage);
  if (!iso) return false;
  return iso.split('T')[0] >= todayISOReunion();
}

/** Jour calendaire Réunion strictement avant aujourd'hui (à La Réunion). */
function isJourCalendrierLocalPasse(dayDate) {
  if (!dayDate) return false;
  return toISODateLocal(dayDate) < todayISOReunion();
}

/** La plage de la réservation tombe sur un jour strictement avant aujourd'hui. */
function reservationPlageEstJourPasse(reservation) {
  const plage = plageDepuisReservation(reservation);
  if (!plage) return true;
  const iso = jourISO(plage);
  if (!iso) return true;
  return iso.split('T')[0] < todayISOReunion();
}

const API_BASE = String(process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(
  /\/$/,
  ''
);
const api = (path) => {
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE}${p}`;
};

function idPlageOuverture(slot) {
  if (!slot) return '';
  const raw = slot._id ?? slot.id;
  if (raw == null) return '';
  return typeof raw === 'object' && typeof raw.toString === 'function'
    ? raw.toString()
    : String(raw);
}

/** Résumés anonymisés des autres adhérents (GET /slots), fusion avec l’objet plage éventuel. */
function autresReservationsAnonymesPourPlage(slot, slotsCharges) {
  const sid = idPlageOuverture(slot);
  if (!sid || !Array.isArray(slotsCharges)) return [];
  const match = slotsCharges.find((s) => String(s._id) === String(sid));
  const arr = match?.autresReservationsAnonymisees ?? slot?.autresReservationsAnonymisees;
  return Array.isArray(arr) ? arr : [];
}

/**
 * Même plage d’ouverture : vos réservations + celles des autres, ordre unique par heure d’arrivée.
 * @returns {Array<{ kind: 'moi', reservation: object } | { kind: 'autre', autre: object, key: string }>}
 */
function fusionReservationsPlageTriHoraire(mesSurPlage, autresSurPlage, sid) {
  /** @type {Array<any>} */
  const items = [];
  for (const r of mesSurPlage) {
    items.push({ kind: 'moi', reservation: r });
  }
  autresSurPlage.forEach((autre, i) => {
    items.push({
      kind: 'autre',
      autre,
      key: `autre-${sid}-${i}-${autre.horaireArriveePrevue}`,
    });
  });
  items.sort((a, b) => {
    const ta =
      a.kind === 'moi'
        ? parseHeureToMinutesBounds(a.reservation?.horaireArriveePrevue)
        : parseHeureToMinutesBounds(a.autre?.horaireArriveePrevue);
    const tb =
      b.kind === 'moi'
        ? parseHeureToMinutesBounds(b.reservation?.horaireArriveePrevue)
        : parseHeureToMinutesBounds(b.autre?.horaireArriveePrevue);
    const na = ta != null ? ta : 24 * 60 + 99;
    const nb = tb != null ? tb : 24 * 60 + 99;
    if (na !== nb) return na - nb;
    if (a.kind !== b.kind) return a.kind === 'moi' ? -1 : 1;
    if (a.kind === 'moi') {
      return String(a.reservation._id).localeCompare(String(b.reservation._id));
    }
    return String(a.key).localeCompare(String(b.key));
  });
  return items;
}

const MINUTES_QUARTS = ['00', '15', '30', '45'];
const HEURES_OPTIONS = Array.from({ length: 24 }, (_, i) =>
  String(i).padStart(2, '0')
);

function parseHeureQuarts(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') {
    return { h: '09', m: '00' };
  }
  const parts = timeStr.trim().split(':');
  const hNum = Math.min(23, Math.max(0, parseInt(parts[0], 10) || 0));
  const h = String(hNum).padStart(2, '0');
  let m = (parts[1] || '00').slice(0, 2);
  if (!MINUTES_QUARTS.includes(m)) {
    m = '00';
  }
  return { h, m };
}

function joinHeure(h, m) {
  return `${h}:${m}`;
}

/** Minutes depuis minuit pour bornes de plage (accepte « HH:MM », pas seulement quarts d’heure). */
function parseHeureToMinutesBounds(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const trimmed = timeStr.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(':');
  const hh = parseInt(parts[0], 10);
  if (Number.isNaN(hh)) return null;
  const h = Math.min(23, Math.max(0, hh));
  const rawM = parts[1] != null ? String(parts[1]) : '0';
  const mm = parseInt(rawM.replace(/^(\d{1,2}).*/, '$1'), 10);
  const m = Number.isNaN(mm) ? 0 : Math.min(59, Math.max(0, mm));
  return h * 60 + m;
}

/** Tri croissant sur l’heure d’arrivée prévue (MM:SS depuis minuit), stable par _id. */
function comparerReservationsParHoraireArrivee(a, b) {
  const ma = parseHeureToMinutesBounds(a?.horaireArriveePrevue);
  const mb = parseHeureToMinutesBounds(b?.horaireArriveePrevue);
  const na = ma != null ? ma : 24 * 60 + 59;
  const nb = mb != null ? mb : 24 * 60 + 59;
  if (na !== nb) return na - nb;
  return String(a?._id ?? '').localeCompare(String(b?._id ?? ''));
}

function plageBoundsMinutes(plage) {
  const d = debutPlage(plage);
  const f = finPlage(plage);
  const startM = d != null && d !== '' ? parseHeureToMinutesBounds(d) : null;
  const endM = f != null && f !== '' ? parseHeureToMinutesBounds(f) : null;
  if (startM == null || endM == null) {
    return { startM: 0, endM: 23 * 60 + 59, crossesMidnight: false };
  }
  return {
    startM,
    endM,
    crossesMidnight: endM < startM,
  };
}

function isTimeMinutesInPlage(totalMinutes, bounds) {
  const { startM, endM, crossesMidnight } = bounds;
  if (crossesMidnight) {
    return totalMinutes >= startM || totalMinutes <= endM;
  }
  return totalMinutes >= startM && totalMinutes <= endM;
}

/** Instant de fin de la plage en heure de La Réunion (traversée de minuit : fin le lendemain). */
function dateFinPlageLocale(slot) {
  const iso = jourISO(slot);
  if (!iso) return null;
  const endM = parseHeureToMinutesBounds(finPlage(slot));
  const startM = parseHeureToMinutesBounds(debutPlage(slot));
  if (endM == null || startM == null) return null;
  const crosses = endM < startM;
  const hh = String(Math.floor(endM / 60)).padStart(2, '0');
  const mm = String(endM % 60).padStart(2, '0');
  return reunionWallClockToDate(iso.split('T')[0], `${hh}:${mm}`, {
    extraDays: crosses ? 1 : 0,
    endOfMinute: true,
  });
}

/** Côté réservation : le créneau ne doit plus être choisi une fois l’heure de fin dépassée à La Réunion. */
function creneauHeureFinPasEncoreDepassee(slot) {
  const fin = dateFinPlageLocale(slot);
  if (!fin) return true;
  return Date.now() <= fin.getTime();
}

/** Au moins une plage encore ouverte au public a une fin d’horaire non dépassée (pour le jour affiché = aujourd’hui à La Réunion). */
function encoreAuMoinsUnCreneauOuvertReservable(dayDate, slotsDuJour) {
  const isoDay = toISODateLocal(dayDate);
  const todayIso = todayISOReunion();
  if (isoDay > todayIso) return true;
  if (isoDay < todayIso) return false;
  const ouverts = slotsDuJour.filter(
    (s) => s.statut === undefined || s.statut === null || s.statut === 'ouvert'
  );
  for (const s of ouverts) {
    if (creneauHeureFinPasEncoreDepassee(s)) return true;
  }
  return false;
}

function getHeuresAutoriseesPlage(plage) {
  const bounds = plageBoundsMinutes(plage);
  return HEURES_OPTIONS.filter((hh) =>
    MINUTES_QUARTS.some((mm) =>
      isTimeMinutesInPlage(parseInt(hh, 10) * 60 + parseInt(mm, 10), bounds)
    )
  );
}

function getMinutesAutoriseesPourHeure(plage, hourStr) {
  const bounds = plageBoundsMinutes(plage);
  const h = parseInt(hourStr, 10);
  if (Number.isNaN(h)) return [...MINUTES_QUARTS];
  return MINUTES_QUARTS.filter((mm) =>
    isTimeMinutesInPlage(h * 60 + parseInt(mm, 10), bounds)
  );
}

/** Premier créneau quart d’heure compris dans la plage (pour valeur initiale). */
function premierQuartHoraireDansPlage(plage) {
  const bounds = plageBoundsMinutes(plage);
  for (let ho = 0; ho < 24; ho++) {
    const hh = String(ho).padStart(2, '0');
    for (const mm of MINUTES_QUARTS) {
      const t = ho * 60 + parseInt(mm, 10);
      if (isTimeMinutesInPlage(t, bounds)) {
        return joinHeure(hh, mm);
      }
    }
  }
  const deb = parseHeureQuarts(debutPlage(plage));
  return joinHeure(deb.h, deb.m);
}

function horaireArriveeValidePourPlage(plage, timeStr) {
  const t = parseHeureToMinutesBounds(timeStr);
  if (t == null) return false;
  return isTimeMinutesInPlage(t, plageBoundsMinutes(plage));
}

/**
 * Si la plage est le jour calendaire courant à La Réunion, l'horaire ne doit pas être avant l'heure actuelle (minute près).
 * Jour futur : pas de contrainte horaire côté « maintenant » (hors bornes de plage).
 */
function horaireArriveePasDansLePasseSiAujourdhui(plage, timeStr) {
  const iso = jourISO(plage);
  if (!iso) return false;
  const t = parseHeureToMinutesBounds(timeStr);
  if (t == null) return false;
  const jourPlage = iso.split('T')[0];
  const jourAujourdhui = todayISOReunion();
  if (jourPlage > jourAujourdhui) return true;
  if (jourPlage < jourAujourdhui) return false;
  return t >= nowMinutesReunion();
}

/** Toutes les plages ouvertes le même jour calendaire que `refPlage` (données déjà chargées). */
function slotsOuvertsMemeJourQue(consultSlots, refPlage) {
  const iso = jourISO(refPlage);
  if (!iso) return [];
  const fromList = consultSlots
    .filter(
      (s) =>
        jourISO(s) === iso &&
        (s.statut === undefined || s.statut === null || s.statut === 'ouvert')
    )
    .sort((a, b) => debutPlage(a).localeCompare(debutPlage(b)));
  if (fromList.length > 0) return fromList;
  if (
    refPlage &&
    jourISO(refPlage) === iso &&
    (refPlage.statut === undefined || refPlage.statut === null || refPlage.statut === 'ouvert')
  ) {
    return [refPlage];
  }
  return [];
}

/**
 * Retourne { max, count, plein, portee } pour une prestation.
 * portee 'jour' : compte toutes les plages du même jour ; 'slot' : ce créneau seulement.
 */
function prestationCapaciteInfo(slot, mesReservations, typeValue, tousLesSlots) {
  const tacheInfo = TACHES.find((t) => t.value === typeValue);
  const max = tacheInfo?.max ?? null;
  const portee = tacheInfo?.portee || 'slot';
  if (!max) return { max: null, count: 0, plein: false, portee };

  if (portee === 'jour') {
    const iso = jourISO(slot);
    const plages = (tousLesSlots || []).filter((s) => jourISO(s) === iso);
    const sidSet = new Set(plages.map((s) => String(idPlageOuverture(s) || '')));
    const slotId = idPlageOuverture(slot);
    if (slot && slotId && !sidSet.has(String(slotId))) {
      plages.push(slot);
    }

    let count = 0;
    for (const s of plages) {
      count += (s.autresReservationsAnonymisees || []).filter((r) =>
        reservationInclutPrestation(r.typesPrestation, typeValue)
      ).length;
    }
    count += mesReservations.filter((r) => {
      if (estReservationAnnuleeOuAbsente(r)) return false;
      const plage = plageDepuisReservation(r);
      if (!plage || jourISO(plage) !== iso) return false;
      const taches = r.prestations || r.taches || [];
      return taches.some((t) => reservationInclutPrestation([t.type], typeValue));
    }).length;

    return { max, count, plein: count >= max, portee };
  }

  const sid = idPlageOuverture(slot);
  const autresCount = (slot.autresReservationsAnonymisees || []).filter((r) =>
    reservationInclutPrestation(r.typesPrestation, typeValue)
  ).length;
  const mesCount = mesReservationsActivesPourSlot(mesReservations, sid).filter((r) => {
    const taches = r.prestations || r.taches || [];
    return taches.some((t) => reservationInclutPrestation([t.type], typeValue));
  }).length;

  const count = autresCount + mesCount;
  return { max, count, plein: count >= max, portee };
}

function responsablesUniquesPourPlages(plages) {
  const byId = new Map();
  for (const s of plages) {
    const r = s.responsable;
    if (!r || typeof r !== 'object') continue;
    const id = String(r._id ?? r.id ?? '');
    if (id && !byId.has(id)) byId.set(id, r);
  }
  return [...byId.values()];
}

function toISODateLocal(d) {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayISOLocal() {
  return todayISOReunion();
}

function isISODateStrictlyPast(iso) {
  if (!iso || typeof iso !== 'string') return false;
  return iso.split('T')[0] < todayISOLocal();
}

const TIMELINE_PX_PER_HOUR = 88;

function hourRangePourPlages(plages) {
  const DEFAULT_START = 7;
  const DEFAULT_END = 20;
  let startHour = DEFAULT_START;
  let endHour = DEFAULT_END;
  for (const p of plages) {
    const b = plageBoundsMinutes(p);
    if (b.startM != null) startHour = Math.min(startHour, Math.floor(b.startM / 60));
    if (b.endM != null) endHour = Math.max(endHour, Math.ceil(b.endM / 60));
  }
  startHour = Math.max(0, startHour);
  endHour = Math.min(24, Math.max(startHour + 1, endHour));
  return { startHour, endHour };
}

/** Cases Date pour la grille mensuelle (jours hors mois inclus, grisés côté UI). */
function getMonthCalendarCells(year, month0) {
  const first = new Date(year, month0, 1);
  const lastDay = new Date(year, month0 + 1, 0).getDate();
  const jsDow = first.getDay();
  const mondayOffset = jsDow === 0 ? 6 : jsDow - 1;
  const cells = [];
  for (let i = mondayOffset; i > 0; i--) {
    cells.push(new Date(year, month0, 1 - i));
  }
  for (let d = 1; d <= lastDay; d++) {
    cells.push(new Date(year, month0, d));
  }
  let next = 1;
  while (cells.length % 7 !== 0) {
    cells.push(new Date(year, month0 + 1, next++));
  }
  return cells;
}

export default function CalendrierReservations({ view }) {
  const { user } = useAuth();
  const [slots, setSlots] = useState([]);
  const [mesReservations, setMesReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  /** Ancre année pour l’onglet liste (Mes réservations). */
  const [currentDate] = useState(() => {
    const p = partsHeureReunion();
    return new Date(p.year, p.month - 1, p.day);
  });
  const [selectedMonth, setSelectedMonth] = useState(() => partsHeureReunion().month - 1);
  const [selectedYear, setSelectedYear] = useState(() => partsHeureReunion().year);
  const [selectedMonthDayIso, setSelectedMonthDayIso] = useState(() => todayISOReunion());
  const [showDayPicker, setShowDayPicker] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(() => partsHeureReunion().month - 1);
  const [pickerYear, setPickerYear] = useState(() => partsHeureReunion().year);
  const [pickerSlots, setPickerSlots] = useState([]);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [showAnnulationModal, setShowAnnulationModal] = useState(false);
  const [reservationAnnulationTarget, setReservationAnnulationTarget] = useState(null);
  const [raisonAnnulation, setRaisonAnnulation] = useState('');
  const [annulationSubmitting, setAnnulationSubmitting] = useState(false);
  /** Réservation dont on affiche le contact du responsable (popup Aide). */
  const [aideResponsableTarget, setAideResponsableTarget] = useState(null);
  /** Étapes 1–5 : infos → créneau → prestation → rucher/horaire → paiement */
  const [reservationModalStep, setReservationModalStep] = useState(1);
  const [selectedSlot, setSelectedSlot] = useState(null);
  /** Plages ouvertes du jour concerné par la popup (choix du créneau). */
  const [modalPlagesDuJour, setModalPlagesDuJour] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [alertes, setAlertes] = useState([]);
  const [montantEstime, setMontantEstime] = useState(null);
  const [soldeAvoir, setSoldeAvoir] = useState(0);
  const [montantAvoirApplique, setMontantAvoirApplique] = useState(0);
  const [montantRestantStripe, setMontantRestantStripe] = useState(0);
  const [couvertParAvoir, setCouvertParAvoir] = useState(false);
  const [estimerLoading, setEstimerLoading] = useState(false);
  /** 'immediat' | 'ulterieur' */
  const [choixPaiement, setChoixPaiement] = useState('ulterieur');
  const [reservationSubmitting, setReservationSubmitting] = useState(false);

  const [reservationForm, setReservationForm] = useState({
    horaireArriveePrevue: '09:00',
  });

  const [currentTache, setCurrentTache] = useState({
    type: '',
    quantitePrevue: 1,
    origineRucher: 'mon',
    autreRucherPrecision: '',
  });

  const isCalendrierTab = view === 'calendrier' || view === 'threeDays' || view === 'week';

  useEffect(() => {
    fetchSlots();
    fetchMesReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, selectedMonth, selectedYear, currentDate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const prepay = params.get('prepay');
    if (prepay === 'success') {
      const sessionId = params.get('session_id');
      const token = localStorage.getItem('token');
      const confirmerEtAfficher = async () => {
        if (sessionId && token) {
          try {
            await axios.post(
              api('/miellerie/balance/confirm-session'),
              { sessionId },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (err) {
            console.error('Confirmation prépay miellerie:', err);
          }
        }
        setSuccess(
          'Paiement en ligne enregistré. Il figure dans l’onglet Mon solde. En cas d’annulation, le montant sera porté en crédit (pas de remboursement carte).'
        );
        fetchMesReservations();
        setTimeout(() => setSuccess(''), 8000);
      };
      confirmerEtAfficher();
      params.delete('prepay');
      params.delete('session_id');
      const qs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    } else if (prepay === 'canceled') {
      setError(
        'Paiement annulé. Votre réservation est bien créée : vous pourrez régler ultérieurement ou reprendre un paiement en ligne si proposé.'
      );
      params.delete('prepay');
      const qs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
      setTimeout(() => setError(''), 8000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSlots = async () => {
    try {
      const token = localStorage.getItem('token');
      const year = isCalendrierTab ? selectedYear : currentDate.getFullYear();
      const month = isCalendrierTab ? selectedMonth + 1 : currentDate.getMonth() + 1;
      const { data } = await axios.get(
        api(`/miellerie/slots?annee=${year}&mois=${month}&statut=ouvert`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSlots(Array.isArray(data) ? data : []);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des horaires d\'ouverture');
      setLoading(false);
    }
  };

  const fetchMesReservations = async () => {
    try {
      const token = localStorage.getItem('token');
      const year = isCalendrierTab ? selectedYear : currentDate.getFullYear();
      const { data } = await axios.get(
        api(`/miellerie/reservations/mes-reservations?annee=${year}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMesReservations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erreur chargement réservations:', err);
    }
  };

  const validateEtape4Reservation = () => {
    if (!selectedSlot) {
      setError("Veuillez sélectionner un créneau d'ouverture.");
      return false;
    }

    if (!currentTache.type) {
      setError('Veuillez sélectionner une prestation');
      return false;
    }

    const options = tachesDisponiblesPourSlot(selectedSlot);
    const tacheInfo = options.find((t) => t.value === currentTache.type);
    if (!tacheInfo) {
      setError('Cette prestation n’est pas proposée sur cette plage d\'ouverture');
      return false;
    }
    if (tacheInfo.needsQuantity && !currentTache.quantitePrevue) {
      setError('Veuillez indiquer une quantité');
      return false;
    }

    if (!['mon', 'sar', 'autre'].includes(currentTache.origineRucher)) {
      setError('Veuillez indiquer le rucher concerné');
      return false;
    }
    if (
      currentTache.origineRucher === 'autre' &&
      !String(currentTache.autreRucherPrecision || '').trim()
    ) {
      setError('Précisez le rucher concerné (saisie libre)');
      return false;
    }

    if (!horaireArriveeValidePourPlage(selectedSlot, reservationForm.horaireArriveePrevue)) {
      setError(
        `L'horaire d'arrivée doit être entre ${debutPlage(selectedSlot)} et ${finPlage(
          selectedSlot
        )} pour le créneau sélectionné.`
      );
      return false;
    }

    const jourSlot = jourISO(selectedSlot);
    if (jourSlot && jourSlot.split('T')[0] < todayISOReunion()) {
      setError('Les réservations ne sont pas possibles sur les jours passés.');
      return false;
    }

    if (!horaireArriveePasDansLePasseSiAujourdhui(selectedSlot, reservationForm.horaireArriveePrevue)) {
      setError(
        "L'horaire d'arrivée prévu ne peut pas être dans le passé (heure de La Réunion). Choisissez une heure à partir de maintenant."
      );
      return false;
    }

    const plageId = idPlageOuverture(selectedSlot);
    if (!plageId) {
      setError('Identifiant de plage d’ouverture manquant. Rechargez la page et réessayez.');
      return false;
    }
    return true;
  };

  const handleReserver = async (modePaiement = 'ulterieur') => {
    if (!validateEtape4Reservation()) return;

    const plageId = idPlageOuverture(selectedSlot);
    setReservationSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        api('/miellerie/reservations'),
        {
          plageOuvertureId: plageId,
          slotId: plageId,
          tache: {
            type: currentTache.type,
            quantitePrevue: currentTache.quantitePrevue || 1,
            origineRucher: currentTache.origineRucher || 'mon',
            ...(currentTache.origineRucher === 'autre'
              ? {
                  autreRucherPrecision: String(currentTache.autreRucherPrecision || '').trim(),
                }
              : {}),
          },
          horaireArriveePrevue: reservationForm.horaireArriveePrevue,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.alertes && data.alertes.length > 0) {
        setAlertes(data.alertes);
      }

      const reservationId = data.reservation?._id;
      if ((modePaiement === 'immediat' || modePaiement === 'solde') && reservationId) {
        const { data: payData } = await axios.post(
          api(`/miellerie/reservations/${reservationId}/pay-now`),
          modePaiement === 'solde' ? { confirmUtiliserSolde: true } : {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (payData.paidWithBalance) {
          setSuccess(
            'Réservation créée et réglée par votre avoir (solde). Le responsable a été notifié.'
          );
          setShowReservationModal(false);
          resetReservationForm();
          fetchSlots();
          fetchMesReservations();
          setTimeout(() => {
            setSuccess('');
            setAlertes([]);
          }, 6000);
          return;
        }

        if (payData.needsBalanceConfirmation) {
          setCouvertParAvoir(true);
          setMontantEstime(payData.montantEstime ?? montantEstime);
          setSoldeAvoir(payData.soldeAvoir ?? soldeAvoir);
          setMontantAvoirApplique(payData.montantAvoirApplique ?? montantAvoirApplique);
          setMontantRestantStripe(0);
          setReservationModalStep(6);
          return;
        }

        if (payData.url) {
          window.location.href = payData.url;
          return;
        }
        const stripe = await stripePromise;
        if (!stripe) {
          setError(
            'Stripe n’est pas configuré (clé publique AMAIR). La réservation a été créée sans paiement.'
          );
          setShowReservationModal(false);
          resetReservationForm();
          fetchSlots();
          fetchMesReservations();
          return;
        }
        const { error: stripeError } = await stripe.redirectToCheckout({
          sessionId: payData.sessionId,
        });
        if (stripeError) {
          setError(stripeError.message);
          fetchMesReservations();
          return;
        }
        return;
      }

      setSuccess('Réservation créée avec succès. Le responsable a été notifié.');
      setShowReservationModal(false);
      resetReservationForm();
      fetchSlots();
      fetchMesReservations();

      setTimeout(() => {
        setSuccess('');
        setAlertes([]);
      }, 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la réservation');
    } finally {
      setReservationSubmitting(false);
    }
  };

  const allerVersEtapePaiementOuConfirmer = async () => {
    setError('');
    if (!validateEtape4Reservation()) return;

    setEstimerLoading(true);
    try {
      const token = localStorage.getItem('token');
      const annee =
        Number(String(jourISO(selectedSlot) || '').slice(0, 4)) || new Date().getFullYear();
      const { data } = await axios.post(
        api('/miellerie/reservations/estimer-montant'),
        {
          type: currentTache.type,
          quantitePrevue: currentTache.quantitePrevue || 1,
          annee,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const montant = typeof data.montantEstime === 'number' ? data.montantEstime : 0;
      setMontantEstime(montant);
      setSoldeAvoir(typeof data.soldeAvoir === 'number' ? data.soldeAvoir : 0);
      setMontantAvoirApplique(
        typeof data.montantAvoirApplique === 'number' ? data.montantAvoirApplique : 0
      );
      setMontantRestantStripe(
        typeof data.montantRestantStripe === 'number' ? data.montantRestantStripe : montant
      );
      setCouvertParAvoir(!!data.couvertParAvoir);
      if (montant <= 0) {
        await handleReserver('ulterieur');
        return;
      }
      setChoixPaiement('ulterieur');
      setReservationModalStep(5);
    } catch (err) {
      setError(err.response?.data?.message || 'Impossible d’estimer le montant');
    } finally {
      setEstimerLoading(false);
    }
  };

  const openAnnulationModal = (reservation) => {
    if (reservationPlageEstJourPasse(reservation)) {
      setError("Impossible d'annuler une réservation sur une date passée.");
      return;
    }
    setAideResponsableTarget(null);
    setReservationAnnulationTarget(reservation);
    setRaisonAnnulation('');
    setError('');
    setShowAnnulationModal(true);
  };

  const openAideResponsable = (reservation) => {
    setError('');
    setAideResponsableTarget(reservation);
  };

  const closeAideResponsable = () => {
    setAideResponsableTarget(null);
  };

  const closeAnnulationModal = () => {
    if (annulationSubmitting) return;
    setShowAnnulationModal(false);
    setReservationAnnulationTarget(null);
    setRaisonAnnulation('');
  };

  const confirmAnnulationReservation = async () => {
    const id = reservationAnnulationTarget?._id;
    if (!id) return;
    if (reservationPlageEstJourPasse(reservationAnnulationTarget)) {
      setError("Impossible d'annuler une réservation sur une date passée.");
      return;
    }
    if (!reservationPeutEtreAnnuleeParUsager(reservationAnnulationTarget)) {
      setError(
        "Annulation en ligne impossible : vérifiez le délai de 48 h avant la plage ou contactez le responsable."
      );
      return;
    }
    if (!raisonAnnulation.trim()) {
      setError("Veuillez indiquer un motif d'annulation.");
      return;
    }
    setAnnulationSubmitting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.delete(api(`/miellerie/reservations/${id}`), {
        headers: { Authorization: `Bearer ${token}` },
        data: { raison: raisonAnnulation.trim() },
      });
      setSuccess('Réservation annulée. Le responsable a été notifié par e-mail.');
      setShowAnnulationModal(false);
      setReservationAnnulationTarget(null);
      setRaisonAnnulation('');
      fetchSlots();
      fetchMesReservations();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'annulation");
    } finally {
      setAnnulationSubmitting(false);
    }
  };

  const resetReservationForm = () => {
    setReservationForm({
      horaireArriveePrevue: '09:00',
    });
    setCurrentTache({
      type: '',
      quantitePrevue: 1,
      origineRucher: 'mon',
      autreRucherPrecision: '',
    });
    setSelectedSlot(null);
    setModalPlagesDuJour(null);
    setReservationModalStep(1);
    setMontantEstime(null);
    setSoldeAvoir(0);
    setMontantAvoirApplique(0);
    setMontantRestantStripe(0);
    setCouvertParAvoir(false);
    setChoixPaiement('ulterieur');
    setEstimerLoading(false);
    setReservationSubmitting(false);
  };

  const closeReservationModal = () => {
    setShowReservationModal(false);
    setAideResponsableTarget(null);
    setError('');
    resetReservationForm();
  };

  const openReservationModal = (slot) => {
    setAideResponsableTarget(null);
    const jourListe = slotsOuvertsMemeJourQue(slots, slot);
    setModalPlagesDuJour(jourListe.length > 0 ? jourListe : [slot]);
    setSelectedSlot(slot);
    setReservationModalStep(1);
    setReservationForm({
      horaireArriveePrevue: '09:00',
    });
    setCurrentTache({
      type: '',
      quantitePrevue: 1,
      origineRucher: 'mon',
      autreRucherPrecision: '',
    });
    setError('');
    setShowReservationModal(true);
  };

  const openReservationModalForDay = (dayDate) => {
    if (isJourCalendrierLocalPasse(dayDate)) {
      setError('Les réservations ne sont pas possibles sur les jours passés.');
      return;
    }
    const key = dayDate.toDateString();
    const sorted = slots
      .filter((slot) => {
        const jd = jourISO(slot);
        if (!jd) return false;
        if (new Date(`${jd}T12:00:00`).toDateString() !== key) return false;
        return slot.statut === undefined || slot.statut === null || slot.statut === 'ouvert';
      })
      .sort((a, b) => debutPlage(a).localeCompare(debutPlage(b)));
    if (sorted.length === 0) return;
    setAideResponsableTarget(null);
    setModalPlagesDuJour(sorted);
    setSelectedSlot(null);
    setReservationModalStep(1);
    setReservationForm({
      horaireArriveePrevue: '09:00',
    });
    setCurrentTache({
      type: '',
      quantitePrevue: 1,
      origineRucher: 'mon',
      autreRucherPrecision: '',
    });
    setError('');
    setShowReservationModal(true);
  };

  const avancerEtapeReservation = () => {
    setError('');
    if (reservationModalStep === 1) {
      setReservationModalStep(2);
    } else if (reservationModalStep === 2) {
      // Étape 2 : créneau → valider le slot choisi
      if (!selectedSlot) {
        setError('Veuillez sélectionner un créneau.');
        return;
      }
      if (!creneauHeureFinPasEncoreDepassee(selectedSlot)) {
        setError("Ce créneau n'est plus disponible : l'heure de fin est dépassée à La Réunion. Choisissez un autre créneau.");
        return;
      }
      // Réinitialiser la prestation si on revient en arrière puis repart
      setCurrentTache({
        type: '',
        quantitePrevue: 1,
        origineRucher: 'mon',
        autreRucherPrecision: '',
      });
      setReservationModalStep(3);
    } else if (reservationModalStep === 3) {
      // Étape 3 : prestation → valider la prestation choisie
      if (!currentTache.type) {
        setError('Veuillez sélectionner une prestation.');
        return;
      }
      if (!tachesDisponiblesPourSlot(selectedSlot).some((t) => t.value === currentTache.type)) {
        setError('Cette prestation n\'est pas proposée sur ce créneau.');
        return;
      }
      setReservationForm({
        horaireArriveePrevue: premierQuartHoraireDansPlage(selectedSlot),
      });
      setReservationModalStep(4);
    }
  };

  const reculerEtapeReservation = () => {
    setError('');
    if (reservationModalStep === 6) {
      setReservationModalStep(5);
    } else if (reservationModalStep === 5) {
      setMontantEstime(null);
      setReservationModalStep(4);
    } else if (reservationModalStep === 4) {
      setReservationModalStep(3);
    } else if (reservationModalStep === 3) {
      // Retour au choix de créneau : réinitialiser la prestation
      setCurrentTache({
        type: '',
        quantitePrevue: 1,
        origineRucher: 'mon',
        autreRucherPrecision: '',
      });
      setReservationModalStep(2);
    } else if (reservationModalStep === 2) {
      // Retour à l'étape 1 : réinitialiser le créneau
      setSelectedSlot(null);
      setReservationModalStep(1);
    }
  };

  const selectPrestationReservation = (typeValue) => {
    setError('');
    setCurrentTache({
      type: typeValue,
      quantitePrevue: 1,
      origineRucher: 'mon',
      autreRucherPrecision: '',
    });
  };

  const selectCreneauReservation = (slot) => {
    setError('');
    setSelectedSlot(slot);
  };

  const getSlotsForDate = (date) => {
    const key = date.toDateString();
    return slots.filter((slot) => {
      const jd = jourISO(slot);
      if (!jd) return false;
      return new Date(`${jd}T12:00:00`).toDateString() === key;
    });
  };

  const slotsByISODate = useMemo(() => {
    const m = new Map();
    for (const slot of slots) {
      const k = jourISO(slot);
      if (!k) continue;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(slot);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => debutPlage(a).localeCompare(debutPlage(b)));
    }
    return m;
  }, [slots]);

  const mesResasActivesByISODate = useMemo(() => {
    const m = new Map();
    for (const r of mesReservations) {
      if (estReservationAnnuleeOuAbsente(r)) continue;
      const plage = plageDepuisReservation(r);
      const iso = plage ? jourISO(plage) : '';
      if (!iso) continue;
      m.set(iso, (m.get(iso) || 0) + 1);
    }
    return m;
  }, [mesReservations]);

  const goToToday = () => {
    const p = partsHeureReunion();
    setSelectedYear(p.year);
    setSelectedMonth(p.month - 1);
    setSelectedMonthDayIso(todayISOReunion());
  };

  const goAdjacentDay = (delta) => {
    const iso = selectedMonthDayIso || todayISOReunion();
    const d = new Date(`${iso}T12:00:00`);
    d.setDate(d.getDate() + delta);
    setSelectedYear(d.getFullYear());
    setSelectedMonth(d.getMonth());
    setSelectedMonthDayIso(toISODateLocal(d));
  };

  const openDayPicker = () => {
    setPickerMonth(selectedMonth);
    setPickerYear(selectedYear);
    setShowDayPicker(true);
  };

  const goPrevPickerMonth = () => {
    if (pickerMonth === 0) {
      setPickerMonth(11);
      setPickerYear((y) => y - 1);
    } else {
      setPickerMonth((mo) => mo - 1);
    }
  };

  const goNextPickerMonth = () => {
    if (pickerMonth === 11) {
      setPickerMonth(0);
      setPickerYear((y) => y + 1);
    } else {
      setPickerMonth((mo) => mo + 1);
    }
  };

  const pickerMonthGrid = useMemo(
    () => getMonthCalendarCells(pickerYear, pickerMonth),
    [pickerYear, pickerMonth]
  );

  const pickerSlotsByISODate = useMemo(() => {
    const map = new Map();
    for (const slot of pickerSlots) {
      const iso = jourISO(slot);
      if (!iso) continue;
      if (!map.has(iso)) map.set(iso, []);
      map.get(iso).push(slot);
    }
    return map;
  }, [pickerSlots]);

  useEffect(() => {
    if (!showDayPicker) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(
          api(`/miellerie/slots?annee=${pickerYear}&mois=${pickerMonth + 1}&statut=ouvert`),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setPickerSlots(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setPickerSlots([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showDayPicker, pickerMonth, pickerYear]);

  /** Panneau toujours ouvert — aujourd’hui si le mois affiché le contient, sinon le 1er */
  useEffect(() => {
    if (!isCalendrierTab || loading) return;

    const slotsMatchMonth =
      slots.length === 0 ||
      slots.some((s) => {
        const iso = jourISO(s);
        if (!iso) return false;
        const [y, m] = iso.split('-').map(Number);
        return y === selectedYear && m === selectedMonth + 1;
      });
    if (slots.length > 0 && !slotsMatchMonth) return;

    const todayIso = todayISOReunion();
    const [ty, tm] = todayIso.split('-').map(Number);
    const monthContainsToday =
      selectedYear === ty && selectedMonth === tm - 1;

    let targetIso = selectedMonthDayIso;
    if (targetIso) {
      const [y, m] = targetIso.split('-').map(Number);
      if (y !== selectedYear || m !== selectedMonth + 1) {
        targetIso = null;
      }
    }
    if (!targetIso) {
      targetIso = monthContainsToday
        ? todayIso
        : toISODateLocal(new Date(selectedYear, selectedMonth, 1));
    }

    if (selectedMonthDayIso !== targetIso) {
      setSelectedMonthDayIso(targetIso);
    }
  }, [
    isCalendrierTab,
    selectedYear,
    selectedMonth,
    selectedMonthDayIso,
    loading,
    slots,
    slotsByISODate,
  ]);

  const selectedDayDate = selectedMonthDayIso
    ? new Date(`${selectedMonthDayIso}T12:00:00`)
    : null;
  const selectedDayLabel = selectedDayDate
    ? selectedDayDate.toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '';

  const mesReservationsAVenir = useMemo(() => {
    const arr = mesReservations.filter(reservationEstActiveEtAVenir);
    arr.sort((a, b) => {
      const pa = plageDepuisReservation(a);
      const pb = plageDepuisReservation(b);
      const isoA = pa ? jourISO(pa) : '';
      const isoB = pb ? jourISO(pb) : '';
      if (isoA !== isoB) return isoA.localeCompare(isoB);
      return comparerReservationsParHoraireArrivee(a, b);
    });
    return arr;
  }, [mesReservations]);

  const heuresPlageModal =
    showReservationModal && reservationModalStep === 4 && selectedSlot
      ? getHeuresAutoriseesPlage(selectedSlot)
      : [];
  const minutesPlageModal =
    showReservationModal && reservationModalStep === 4 && selectedSlot
      ? getMinutesAutoriseesPourHeure(
          selectedSlot,
          parseHeureQuarts(reservationForm.horaireArriveePrevue).h
        )
      : [];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honey-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Entête : uniquement vue calendrier (l’onglet « Mes réservations » a son propre titre) */}
      {isCalendrierTab && (
        <p className="text-slate-600 text-base leading-relaxed">
          Choisissez un horaire d&apos;arrivée pendant les plages d&apos;ouverture, dans la limite des quotas indiqués pour chaque prestation.
        </p>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircleIcon className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-green-800 font-medium">Succès</p>
            <p className="text-green-700 text-sm">{success}</p>
          </div>
          <button onClick={() => setSuccess('')} className="text-green-600 hover:text-green-800">×</button>
        </div>
      )}

      {alertes.length > 0 && (
        <div className="bg-honey-50 border border-honey-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-honey-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-honey-800 font-medium">Alertes</p>
              <ul className="text-honey-700 text-sm mt-1 space-y-1">
                {alertes.map((alerte, i) => (
                  <li key={i}>• {alerte}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Vue planning du jour */}
      {isCalendrierTab && selectedMonthDayIso && selectedDayDate && (
        <div className="rounded-2xl bg-slate-50/80 space-y-4">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="flex flex-col items-center justify-center w-16 h-16 rounded-xl bg-honey-50 text-honey-700 shrink-0">
                  <span className="text-2xl font-bold leading-none tabular-nums">
                    {parseInt(selectedMonthDayIso.split('-')[2], 10)}
                  </span>
                  <span className="text-[12px] font-semibold uppercase tracking-wide mt-0.5">
                    {selectedDayDate.toLocaleDateString('fr-FR', { weekday: 'short' })}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 capitalize leading-snug">
                    {selectedDayLabel}
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    {(slotsByISODate.get(selectedMonthDayIso) || []).length} créneau
                    {(slotsByISODate.get(selectedMonthDayIso) || []).length !== 1 ? 'x' : ''} d&apos;ouverture
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0 w-full sm:w-auto">
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => goAdjacentDay(-1)}
                    className="p-2 sm:p-1.5 rounded-lg text-honey-600 hover:bg-honey-50 transition-colors"
                    aria-label="Jour précédent"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={goToToday}
                    className="px-2.5 py-1 text-xs font-semibold text-honey-700 bg-honey-50 border border-honey-200 rounded-lg hover:bg-honey-100 transition-colors"
                  >
                    Aujourd&apos;hui
                  </button>
                  <button
                    type="button"
                    onClick={() => goAdjacentDay(1)}
                    className="p-2 sm:p-1.5 rounded-lg text-honey-600 hover:bg-honey-50 transition-colors"
                    aria-label="Jour suivant"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={openDayPicker}
                  className="inline-flex flex-1 sm:flex-none items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 shadow-sm transition-colors"
                >
                  <CalendarDaysIcon className="h-4 w-4" />
                  <span className="sm:hidden">Autre jour</span>
                  <span className="hidden sm:inline">Choisir un autre jour</span>
                </button>
                {selectedDayDate &&
                  !isJourCalendrierLocalPasse(selectedDayDate) &&
                  encoreAuMoinsUnCreneauOuvertReservable(
                    selectedDayDate,
                    getSlotsForDate(selectedDayDate)
                  ) && (
                    <button
                      type="button"
                      onClick={() => openReservationModalForDay(selectedDayDate)}
                      className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-honey-800 bg-honey-50 border border-honey-200 rounded-xl hover:bg-honey-100 shadow-sm transition-colors"
                    >
                      <PlusIcon className="h-4 w-4 shrink-0" />
                      Réserver
                    </button>
                  )}
              </div>
            </div>
          </div>

          {(() => {
            const daySlotsOuverts = getSlotsForDate(selectedDayDate);
            const sortedDaySlots = plagesJourAvecReservationsUtilisateur(
              selectedDayDate,
              daySlotsOuverts,
              mesReservations
            );
            const jourPasse = isJourCalendrierLocalPasse(selectedDayDate);
            const aOuverturePublique = daySlotsOuverts.some(
              (s) => s.statut === undefined || s.statut === null || s.statut === 'ouvert'
            );
            const peutEncoreReserverCeJour = encoreAuMoinsUnCreneauOuvertReservable(
              selectedDayDate,
              daySlotsOuverts
            );
            const { startHour, endHour } = hourRangePourPlages(sortedDaySlots);
            const hours = [];
            for (let h = startHour; h <= endHour; h++) hours.push(h);
            const timelineHeight = Math.max(1, endHour - startHour) * TIMELINE_PX_PER_HOUR;
            const nowP = partsHeureReunion();
            const isToday = selectedMonthDayIso === todayISOReunion();
            const nowTop =
              ((nowP.hour * 60 + nowP.minute) / 60 - startHour) * TIMELINE_PX_PER_HOUR;
            const showNowLine = isToday && nowTop >= 0 && nowTop <= timelineHeight;

            const renderReservationEntry = (entry) => {
              if (entry.kind === 'moi') {
                const r = entry.reservation;
                const prestas = r.prestations || r.taches || [];
                const t0 = prestas[0];
                const libPresta = t0 ? libelleTacheType(t0.type) : '';
                const hh = (r.horaireArriveePrevue || '—').split(':')[0];
                const mm = (r.horaireArriveePrevue || '').includes(':')
                  ? r.horaireArriveePrevue.split(':')[1]
                  : '';
                return (
                  <div
                    key={String(r._id)}
                    className="flex items-center gap-2 w-full rounded-lg bg-white shadow-sm border border-green-100 pr-2"
                  >
                    <div className="text-xs font-bold shrink-0 flex items-center justify-center rounded-md bg-green-50 text-green-800 px-2 sm:px-3 py-2">
                      <span className="tabular-nums leading-none">{hh}</span>
                      <span className="tabular-nums">:</span>
                      {mm ? (
                        <span className="tabular-nums">{mm}</span>
                      ) : null}
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center">
                      <p className="font-bold text-slate-900 truncate text-sm">
                        {user ? `${user.prenom || ''} ${user.nom || ''}`.trim() : 'Vous'}
                      </p>
                      {libPresta ? (
                        <>
                          <p className="hidden sm:block text-xs text-slate-300 px-1.5">|</p>
                          <p className="text-xs sm:text-sm font-medium text-slate-500 truncate">{libPresta}</p>
                        </>
                      ) : null}
                      {t0 ? (
                        <>
                          <p className="hidden sm:block text-xs text-slate-300 px-1.5">|</p>
                          <p className="hidden sm:block text-sm text-slate-500 truncate">{libelleOrigineRucher(t0)}</p>
                        </>
                      ) : null}
                    </div>
                    {reservationPeutEtreAnnuleeParUsager(r) && (
                      <button
                        type="button"
                        onClick={() => openAnnulationModal(r)}
                        className="shrink-0 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 px-2 py-1 rounded-md"
                      >
                        Annuler
                      </button>
                    )}
                  </div>
                );
              }
              const { autre, key } = entry;
              const libelles = (autre.typesPrestation || [])
                .map((t) => libelleTacheType(t))
                .filter(Boolean);
              const hh = (autre.horaireArriveePrevue || '—').split(':')[0];
              const mm = (autre.horaireArriveePrevue || '').includes(':')
                ? autre.horaireArriveePrevue.split(':')[1]
                : '';
              return (
                <div
                  key={key}
                  className="flex items-center gap-2 w-full rounded-lg bg-white shadow-sm border border-slate-100"
                >
                  <div className="text-xs font-bold shrink-0 flex items-center justify-center rounded-md bg-slate-50 text-slate-700 px-2 sm:px-3 py-2">
                    <span className="tabular-nums text-center leading-none">{hh}</span>
                    <span className="tabular-nums">:</span>
                    {mm ? (
                      <span className="tabular-nums text-center">{mm}</span>
                    ) : null}
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center">
                    <p className="font-bold text-slate-900 truncate text-sm">
                      {autre.prenomNom || 'Autre adhérent'}
                    </p>
                    {libelles.length > 0 ? (
                      <>
                        <p className="hidden sm:block text-xs text-slate-300 px-1.5">|</p>
                        <p className="text-xs sm:text-sm font-medium text-slate-500 truncate">{libelles.join(', ')}</p>
                      </>
                    ) : null}
                  </div>
                </div>
              );
            };

            return (
              <>
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                  <div className="relative" style={{ height: timelineHeight + 16 }}>
                    {hours.map((h, i) => (
                      <div
                        key={h}
                        className="absolute left-0 right-0 flex items-start pointer-events-none"
                        style={{ top: i * TIMELINE_PX_PER_HOUR + 8 }}
                      >
                        <span className="w-10 sm:w-14 shrink-0 -mt-2 pr-1.5 sm:pr-2 text-right text-[10px] sm:text-[11px] font-semibold text-slate-400 tabular-nums">
                          {String(h).padStart(2, '0')}:00
                        </span>
                        <div className="flex-1 border-t border-slate-100" />
                      </div>
                    ))}

                    {showNowLine && (
                      <div
                        className="absolute left-10 sm:left-14 right-0 z-20 flex items-center pointer-events-none"
                        style={{ top: nowTop + 8 }}
                      >
                        <span className="w-2 h-2 rounded-full bg-red-500 -ml-1" />
                        <span className="text-[10px] font-bold text-red-600 bg-white/90 px-1 rounded mr-1 tabular-nums">
                          {formatHeureReunion()}
                        </span>
                        <div className="flex-1 border-t-2 border-red-400" />
                      </div>
                    )}

                    {sortedDaySlots.length === 0 ? (
                      <div className="absolute inset-0 flex items-center justify-center pl-10 sm:pl-14">
                        <div className="text-center px-6">
                          <ClockIcon className="h-8 w-8 text-slate-300 mx-auto mb-2" aria-hidden />
                          <p className="text-sm text-slate-500">Pas d&apos;ouverture ce jour</p>
                        </div>
                      </div>
                    ) : (
                      sortedDaySlots.map((slot) => {
                        const sid = idPlageOuverture(slot);
                        const bounds = plageBoundsMinutes(slot);
                        const startM = bounds.startM ?? startHour * 60;
                        const endM = bounds.endM ?? endHour * 60;
                        const top =
                          ((startM / 60) - startHour) * TIMELINE_PX_PER_HOUR + 8;
                        const height = Math.max(
                          148,
                          ((endM - startM) / 60) * TIMELINE_PX_PER_HOUR - 6
                        );
                        const mesSurPlage = mesReservationsActivesPourSlot(mesReservations, sid);
                        const autresSurPlage = autresReservationsAnonymesPourPlage(slot, slots);
                        const rangSurPlage = fusionReservationsPlageTriHoraire(
                          mesSurPlage,
                          autresSurPlage,
                          sid
                        );
                        const resp = slot.responsable;
                        const slotOuvert =
                          slot.statut === undefined || slot.statut === null || slot.statut === 'ouvert';
                        const slotReservable =
                          slotOuvert &&
                          !jourPasse &&
                          creneauHeureFinPasEncoreDepassee(slot);
                        return (
                          <div
                            key={sid || JSON.stringify(slot)}
                            className="absolute left-11 sm:left-16 right-2 sm:right-3 z-10 flex flex-row rounded-2xl bg-honey-50/70 border border-honey-200/80 shadow-sm overflow-hidden"
                            style={{ top, height }}
                          >
                            <div className="w-[7.25rem] sm:w-52 shrink-0 flex flex-col   gap-1.5 sm:gap-2 px-2 sm:px-3.5 py-2 sm:py-3 border-r border-honey-200/70">
                              <div className="min-w-0">
                                <span className="font-semibold text-slate-900 tabular-nums text-sm sm:text-base">
                                  {debutPlage(slot)} – {finPlage(slot)}
                                </span>
                                {resp && typeof resp === 'object' && (resp.prenom || resp.nom) && (
                                  <p className="text-sm sm:text-xs text-slate-500 mt-1 leading-snug">
                                    <span className="hidden sm:inline">Responsable : </span>
                                    {[resp.prenom, resp.nom].filter(Boolean).join(' ')}
                                  </p>
                                )}
                                {slot.notes?.trim() && (
                                  <p className="text-[11px] sm:text-xs text-slate-500 italic mt-1 line-clamp-2">{slot.notes}</p>
                                )}
                              </div>
                              {slotReservable && (
                                <button
                                  type="button"
                                  onClick={() => openReservationModal(slot)}
                                  className="w-full inline-flex self-start text-sm items-center px-4 py-2 bg-honey-600 text-white rounded-lg hover:bg-honey-700 transition-colors whitespace-nowrap"

                                >
                                  <PlusIcon className="h-3.5 w-3.5 shrink-0" />
                                  <span className="sm:hidden">Réserver</span>
                                  <span className="hidden sm:inline">Réserver un créneau</span>
                                </button>
                              )}
                            </div>
                            <div className="flex-1 min-h-0 min-w-0 overflow-y-auto px-2.5 py-2.5 space-y-2">
                              {rangSurPlage.length === 0 ? (
                                <div className="h-full min-h-[4.5rem] flex items-center justify-center rounded-xl bg-white/70 border border-dashed border-honey-200 px-3.5 py-3">
                                  <p className="text-sm text-slate-500 text-center">Aucune réservation</p>
                                </div>
                              ) : (
                                rangSurPlage.map(renderReservationEntry)
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {aOuverturePublique ? (
                  jourPasse ? (
                    <p className="text-sm text-slate-500 text-center py-2.5 px-1 leading-snug bg-white rounded-xl border border-slate-100">
                      Jour passé — réservation impossible.
                    </p>
                  ) : !peutEncoreReserverCeJour ? (
                    <p className="text-sm text-slate-600 text-center py-2.5 px-1 leading-snug bg-white rounded-xl border border-slate-100">
                      Réservations terminées pour aujourd&apos;hui.
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openReservationModalForDay(selectedDayDate)}
                      className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-honey-800 bg-white border border-honey-200 rounded-xl hover:bg-honey-50 shadow-sm transition-colors"
                    >
                      <PlusIcon className="h-4 w-4 shrink-0" />
                      Réserver
                    </button>
                  )
                ) : sortedDaySlots.length > 0 ? (
                  <p className="text-sm text-slate-500 text-center py-2.5 px-1 bg-white rounded-xl border border-slate-100">
                    Fermé au public — réservations visibles ci-dessus.
                  </p>
                ) : null}
              </>
            );
          })()}
        </div>
      )}

      {showDayPicker && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/40"
          onClick={() => setShowDayPicker(false)}
        >
          <div
            className="w-full sm:max-w-xl max-h-[90vh] overflow-y-auto bg-white rounded-t-2xl sm:rounded-2xl shadow-xl border border-slate-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-50">
              <h3 className="text-base font-bold text-slate-900">Choisir un jour</h3>
              <button
                type="button"
                onClick={() => setShowDayPicker(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-50 hover:text-slate-600"
                aria-label="Fermer"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="flex items-center justify-between px-5 py-3">
              <button
                type="button"
                onClick={goPrevPickerMonth}
                className="p-1.5 rounded-lg text-honey-600 hover:bg-honey-50 transition-colors"
                aria-label="Mois précédent"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <span className="text-sm font-semibold text-honey-700 capitalize min-w-[9.5rem] text-center">
                {new Date(pickerYear, pickerMonth, 1).toLocaleDateString('fr-FR', {
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
              <button
                type="button"
                onClick={goNextPickerMonth}
                className="p-1.5 rounded-lg text-honey-600 hover:bg-honey-50 transition-colors"
                aria-label="Mois suivant"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-7 px-3 sm:px-4">
              {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((label) => (
                <div
                  key={label}
                  className="text-center text-[11px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider py-2"
                >
                  {label}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-y-1 px-2 sm:px-3 pb-4">
              {pickerMonthGrid.map((dayDate, idx) => {
                const iso = toISODateLocal(dayDate);
                const inMonth = dayDate.getMonth() === pickerMonth;
                const daySlots = inMonth ? pickerSlotsByISODate.get(iso) || [] : [];
                const myCount = inMonth ? mesResasActivesByISODate.get(iso) || 0 : 0;
                const hasSlots = daySlots.length > 0;
                const hasMyResas = myCount > 0;
                const isSelected = selectedMonthDayIso === iso;
                const isPast = isISODateStrictlyPast(iso);

                return (
                  <button
                    key={`${iso}-${idx}`}
                    type="button"
                    onClick={() => {
                      setSelectedYear(dayDate.getFullYear());
                      setSelectedMonth(dayDate.getMonth());
                      setSelectedMonthDayIso(iso);
                      setShowDayPicker(false);
                    }}
                    className="relative flex flex-col items-center gap-1 py-1.5 px-0.5 min-h-[3.5rem] rounded-xl transition-colors hover:bg-slate-50/80"
                  >
                    <span className="relative inline-flex">
                      <span
                        className={`inline-flex items-center justify-center w-9 h-9 rounded-xl text-sm font-semibold tabular-nums transition-colors ${
                          !inMonth
                            ? 'text-slate-300'
                            : isSelected
                              ? 'bg-honey-600 text-white shadow-sm'
                              : isPast
                                ? hasMyResas
                                  ? 'text-slate-500 ring-1 ring-honey-300 bg-slate-100/80'
                                  : hasSlots
                                    ? 'text-slate-400'
                                    : 'text-slate-400 bg-slate-100/60'
                                : hasMyResas
                                  ? 'text-honey-700 ring-1 ring-honey-400 bg-honey-50/40'
                                  : hasSlots
                                    ? 'text-slate-800 ring-1 ring-slate-200'
                                    : 'text-slate-700'
                        }`}
                      >
                        {dayDate.getDate()}
                      </span>
                      {inMonth && hasMyResas && (
                        <span
                          className={`absolute -top-1 -right-1 z-[1] inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-0.5 rounded-full text-[9px] font-bold text-white tabular-nums shadow-sm ${
                            isSelected ? 'bg-honey-500' : isPast ? 'bg-slate-400' : 'bg-honey-500'
                          }`}
                        >
                          {myCount}
                        </span>
                      )}
                    </span>
                    {inMonth && hasSlots && (
                      <div className="w-1.5 h-1.5 rounded-full bg-honey-400" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Vue Liste */}
      {view === 'list' && (
        <div className="rounded-2xl bg-slate-50/80 space-y-3">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-slate-900">Mes réservations à venir</h2>
            <span className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full bg-honey-100 text-honey-800 text-sm font-bold tabular-nums">
              {mesReservationsAVenir.length}
            </span>
          </div>

          {mesReservationsAVenir.length === 0 ? (
            <div className="rounded-2xl bg-white shadow-sm border border-slate-100 px-4 py-10 text-center">
              <p className="text-sm text-slate-400">Aucune réservation à venir</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {mesReservationsAVenir.map((reservation) => {
                const plageR = plageDepuisReservation(reservation);
                const iso = plageR ? jourISO(plageR) : '';
                const dayNum = iso ? parseInt(iso.split('-')[2], 10) : null;
                const weekdayShort = iso
                  ? new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short' })
                  : '';
                const moisShort = iso
                  ? new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { month: 'short' })
                  : '';
                const prestas = reservation.prestations || reservation.taches || [];
                const t0 = prestas[0];
                const info = t0
                  ? TACHES.find((t) => t.value === (t0.type === 'extraction' ? 'extraction_hausses' : t0.type))
                  : null;
                const hh = (reservation.horaireArriveePrevue || '—').split(':')[0];
                const mm = (reservation.horaireArriveePrevue || '').includes(':')
                  ? reservation.horaireArriveePrevue.split(':')[1]
                  : '';
                const resp = plageR?.responsable;
                const nomResponsable =
                  resp && typeof resp === 'object'
                    ? [resp.prenom, resp.nom].filter(Boolean).join(' ')
                    : '';

                return (
                  <div
                    key={reservation._id}
                    className="rounded-2xl bg-white shadow-sm border border-slate-200 p-4 flex flex-col gap-3 min-h-[11.5rem]"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="flex flex-col items-center justify-center w-14 h-14 rounded-xl bg-honey-50 text-honey-700 shrink-0">
                          <span className="text-lg font-bold leading-none tabular-nums">
                            {dayNum ?? '—'}
                          </span>
                          <span className="text-[11px] font-semibold uppercase tracking-wide mt-0.5">
                            {moisShort || weekdayShort}
                          </span>
                        </div>
                        <div className="min-w-0">
                          {/* <p className="font-semibold text-slate-900 text-sm capitalize truncate">
                            {dateLocalePlage(plageR)}
                          </p> */}
                          <p className="text-sm text-slate-500 tabular-nums mt-0.5">
                            Créneau :{' '}
                            <span className="font-medium text-slate-700">
                              {debutPlage(plageR)} – {finPlage(plageR)}
                            </span>
                          </p>
                          <p className="text-sm text-slate-500 mt-0.5 truncate">
                            Responsable :{' '}
                            <span className="font-medium text-slate-700">
                              {nomResponsable || 'Non renseigné'}
                            </span>
                          </p>
                        </div>
                      </div>
                      <div className="shrink-0 flex flex-col items-end gap-1">
                      <span
                        className={`inline-flex justify-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          reservation.statut === 'reservee'
                            ? 'bg-blue-100 text-blue-800'
                            : reservation.statut === 'honoree'
                              ? 'bg-green-100 text-green-800'
                              : reservation.statut === 'absente'
                                ? 'bg-honey-100 text-honey-900'
                                : reservation.statut === 'annulee'
                                  ? 'bg-red-50 text-red-700'
                                  : 'bg-slate-100 text-slate-800'
                        }`}
                      >
                        {libelleStatutReservation(reservation.statut, reservation)}
                      </span>
                      {reservation.paiement?.statut === 'paye' &&
                        ['stripe', 'solde'].includes(reservation.paiement?.methode) &&
                        Number(reservation.montantPrepaye) > 0 && (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-800">
                            {reservation.paiement?.methode === 'solde'
                              ? `Réglé par avoir · ${Number(reservation.montantPrepaye).toFixed(2)} €`
                              : Number(reservation.montantAvoirUtilise) > 0
                                ? `Payé · ${Number(reservation.montantPrepaye).toFixed(2)} € (dont avoir)`
                                : `Payé en ligne · ${Number(reservation.montantPrepaye).toFixed(2)} €`}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2.5">
                      <div className="shrink-0 flex flex-col items-center justify-center rounded-xl w-14 h-14 bg-green-50 text-green-800">
                        <span className="text-base font-bold tabular-nums leading-none">{hh}</span>
                        {mm ? (
                          <span className="text-sm font-semibold tabular-nums mt-0.5">{mm}</span>
                        ) : null}
                      </div>
                      <div className="min-w-0 flex-1">
                        {t0 ? (
                          <p className="text-sm font-medium text-slate-700 truncate">
                            <b>{libelleTacheType(t0.type)}</b>
                            {info?.needsQuantity && t0.quantitePrevue != null
                              ? ` · ${libelleQuantiteUnite(t0.quantitePrevue, info)}`
                              : ''}
                          </p>
                        ) : (
                          <p className="text-sm text-slate-400">Prestation non renseignée</p>
                        )}
                        {t0 ? (
                          <p className="text-sm text-slate-500 mt-0.5 truncate">
                            {libelleOrigineRucher(t0)}
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <div className="mt-auto flex gap-1.5 pt-1">
                      {reservation.statut === 'honoree' ? (
                        <div className="flex-1 text-center text-sm font-semibold text-green-800 bg-green-50 border border-green-200 px-2.5 py-1.5 rounded-md">
                          Réservation passée
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => openAideResponsable(reservation)}
                            className="flex-1 text-sm font-semibold text-slate-700 bg-slate-50 hover:bg-slate-100 border border-slate-200 px-2.5 py-1.5 rounded-md"
                          >
                            Contacter
                          </button>
                          <button
                            type="button"
                            onClick={() => openAnnulationModal(reservation)}
                            className="flex-1 text-sm font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 px-2.5 py-1.5 rounded-md"
                          >
                            Annuler
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modal Réservation — étapes 1 à 5 */}
      {showReservationModal && modalPlagesDuJour && modalPlagesDuJour.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full my-8">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">
                Nouvelle réservation ({dateLocalePlage(modalPlagesDuJour[0])})
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                Étape {Math.min(reservationModalStep, 5)} sur 5
                {reservationModalStep === 6 ? ' — Confirmation avoir' : ''}
                {reservationModalStep < 6 ? ' — ' : ''}
                {reservationModalStep === 1 && 'Informations'}
                {reservationModalStep === 2 && 'Choisir un créneau'}
                {reservationModalStep === 3 && 'Choisir une prestation'}
                {reservationModalStep === 4 && 'Rucher et horaire'}
                {reservationModalStep === 5 && 'Paiement'}
              </p>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (reservationModalStep === 5) {
                  if (choixPaiement === 'immediat' && couvertParAvoir) {
                    setReservationModalStep(6);
                    return;
                  }
                  handleReserver(choixPaiement === 'immediat' ? 'immediat' : 'ulterieur');
                  return;
                }
                if (reservationModalStep === 6) {
                  handleReserver('solde');
                }
              }}
              className="p-6 space-y-6"
            >
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <XCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-red-800 font-medium text-sm">Erreur</p>
                    <p className="text-red-700 text-sm mt-0.5">{error}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setError('')}
                    className="text-red-600 hover:text-red-800 shrink-0"
                    aria-label="Fermer le message"
                  >
                    ×
                  </button>
                </div>
              )}

              {reservationModalStep === 1 && (
                <div className="space-y-4">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3 text-sm text-slate-700">
                    <p className="font-semibold text-slate-900">Responsable(s)</p>
                    {responsablesUniquesPourPlages(modalPlagesDuJour).length === 0 ? (
                      <p className="text-slate-500">Non renseigné pour ces créneaux.</p>
                    ) : (
                      <ul className="space-y-2">
                        {responsablesUniquesPourPlages(modalPlagesDuJour).map((r) => (
                          <li key={String(r._id ?? r.email ?? `${r.prenom}-${r.nom}`)}>
                            <span className="font-medium text-slate-800">
                              {r.prenom} {r.nom}
                            </span>
                            {r.telephone && (
                              <span className="block text-slate-600">Tél. {r.telephone}</span>
                            )}
                            {r.email && <span className="block text-slate-600">{r.email}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="rounded-lg border border-honey-200 bg-honey-50 p-4 text-sm text-honey-950">
                    <p className="font-semibold">Horaires</p>
                    <p className="mt-1 text-honey-900">
                      Tous les horaires sont indiqués à l&apos;heure de La Réunion
                      (actuellement {formatHeureReunion()}).
                    </p>
                    <p className="font-semibold mt-3">Conditions d&apos;annulation</p>
                    <ul className="mt-2 list-disc list-inside space-y-1 text-honey-900">
                      <li>
                        Annulation avec <strong>motif</strong> possible tant qu&apos;il
                        reste plus de <strong>48h</strong> avant le créneau réservé.
                      </li>
                      <li>
                        À moins de 48 h, contactez le responsable du créneau (coordonnées ci-dessus).
                      </li>
                      <li>Le responsable reçoit un e-mail automatique lors d&apos;une annulation en ligne.</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Étape 2 : choix du créneau (avant la prestation) */}
              {reservationModalStep === 2 && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-slate-700">Sélectionner un créneau d&apos;ouverture</p>
                  <div className="grid grid-cols-1 gap-2">
                    {[...modalPlagesDuJour]
                      .sort((a, b) => debutPlage(a).localeCompare(debutPlage(b)))
                      .map((s) => {
                        const sid = idPlageOuverture(s);
                        const dansLesTemps = creneauHeureFinPasEncoreDepassee(s);
                        const sel = selectedSlot && idPlageOuverture(selectedSlot) === sid;
                        const prestationsSlot = tachesDisponiblesPourSlot(s);
                        return (
                          <button
                            key={sid}
                            type="button"
                            disabled={!dansLesTemps}
                            onClick={() => dansLesTemps && selectCreneauReservation(s)}
                            className={`rounded-lg border px-4 py-3 text-left transition-colors ${
                              !dansLesTemps
                                ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed opacity-80'
                                : sel
                                  ? 'border-honey-500 bg-honey-50 ring-2 ring-honey-400 text-slate-900'
                                  : 'border-slate-200 bg-white hover:border-honey-300 hover:bg-slate-50 text-slate-900'
                            }`}
                          >
                            <span className={`font-semibold ${!dansLesTemps ? 'text-slate-500' : 'text-slate-900'}`}>
                              {debutPlage(s)} – {finPlage(s)}
                            </span>
                            {!dansLesTemps ? (
                              <span className="block text-sm mt-1 text-slate-400">
                                Créneau terminé — l&apos;heure de fin est dépassée à La Réunion
                              </span>
                            ) : (
                              <>
                                {s.responsable && (
                                  <span className="block text-sm text-slate-600 mt-1">
                                    Responsable : {s.responsable.prenom} {s.responsable.nom}
                                  </span>
                                )}
                                {prestationsSlot.length > 0 && (
                                  <span className="block text-xs text-slate-500 mt-1">
                                    {prestationsSlot.map((t) => t.label).join(' · ')}
                                  </span>
                                )}
                              </>
                            )}
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Étape 3 : choix de la prestation (après le créneau, avec info capacité) */}
              {reservationModalStep === 3 && selectedSlot && (
                <div className="space-y-3">
                  <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                    <span className="font-medium text-slate-900">Créneau sélectionné :</span>{' '}
                    {debutPlage(selectedSlot)} – {finPlage(selectedSlot)}
                  </div>
                  <p className="text-sm font-medium text-slate-700">Sélectionner une prestation</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {tachesDisponiblesPourSlot(selectedSlot).map((t) => {
                      const { max, count, plein, portee } = prestationCapaciteInfo(
                        selectedSlot,
                        mesReservations,
                        t.value,
                        slots
                      );
                      const libellePortee = portee === 'jour' ? 'ce jour' : 'ce créneau';
                      return (
                        <button
                          key={t.value}
                          type="button"
                          disabled={plein}
                          onClick={() => !plein && selectPrestationReservation(t.value)}
                          className={`rounded-lg border px-4 py-3 text-left text-sm font-medium transition-colors ${
                            plein
                              ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed opacity-80'
                              : currentTache.type === t.value
                                ? 'border-honey-500 bg-honey-50 text-honey-900 ring-2 ring-honey-400'
                                : 'border-slate-200 bg-white hover:border-honey-300 hover:bg-honey-50/50 text-slate-800'
                          }`}
                        >
                          {t.label}
                          {max != null && (
                            <span className={`block text-xs mt-0.5 font-normal ${plein ? 'text-red-500' : 'text-slate-500'}`}>
                              {plein
                                ? `Complet ${libellePortee} (${max}/${max})`
                                : count === 0
                                  ? `Disponible (0/${max} ${portee === 'jour' ? 'par jour' : 'par créneau'})`
                                  : `${count}/${max} ${libellePortee} — encore disponible`}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {tachesDisponiblesPourSlot(selectedSlot).length === 0 && (
                    <p className="text-sm text-honey-800 bg-honey-50 border border-honey-200 rounded-lg px-3 py-2">
                      Ce créneau ne propose aucune prestation. Revenez en arrière pour choisir un autre créneau.
                    </p>
                  )}
                </div>
              )}

              {reservationModalStep === 4 && selectedSlot && (
                <div className="space-y-6">
                  <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-slate-700">
                    <span className="font-medium text-slate-900">Créneau retenu :</span>{' '}
                    {debutPlage(selectedSlot)} – {finPlage(selectedSlot)}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {currentTache.type &&
                      tachesDisponiblesPourSlot(selectedSlot).find((t) => t.value === currentTache.type)
                        ?.needsQuantity && (
                        <div className="sm:col-span-2">
                          <label className="block font-medium text-slate-700 mb-2">
                            Quantité (
                            {
                              tachesDisponiblesPourSlot(selectedSlot).find(
                                (t) => t.value === currentTache.type
                              )?.unit
                            }
                            )
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={currentTache.quantitePrevue}
                            onChange={(e) =>
                              setCurrentTache({
                                ...currentTache,
                                quantitePrevue: parseInt(e.target.value, 10) || 1,
                              })
                            }
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 focus:border-honey-500"
                            required
                          />
                        </div>
                      )}
                    {currentTache.type &&
                      (() => {
                        const td = tachesDisponiblesPourSlot(selectedSlot).find(
                          (t) => t.value === currentTache.type
                        );
                        if (!td?.aide) return null;
                        return (
                          <p className="sm:col-span-2 text-sm text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                            {td.aide}
                          </p>
                        );
                      })()}
                  </div>

                  <fieldset className="space-y-2 border-0 p-0 m-0">
                    <legend className="text-sm font-medium text-slate-700 mb-2">Rucher concerné</legend>
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                      <input
                        type="radio"
                        name="rucherReservation"
                        checked={currentTache.origineRucher === 'mon'}
                        onChange={() =>
                          setCurrentTache({
                            ...currentTache,
                            origineRucher: 'mon',
                            autreRucherPrecision: '',
                          })
                        }
                        className="border-slate-300 text-honey-600 focus:ring-honey-500"
                      />
                      Mon rucher
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                      <input
                        type="radio"
                        name="rucherReservation"
                        checked={currentTache.origineRucher === 'sar'}
                        onChange={() =>
                          setCurrentTache({
                            ...currentTache,
                            origineRucher: 'sar',
                            autreRucherPrecision: '',
                          })
                        }
                        className="border-slate-300 text-honey-600 focus:ring-honey-500"
                      />
                      Un rucher géré par le SAR
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
                      <input
                        type="radio"
                        name="rucherReservation"
                        checked={currentTache.origineRucher === 'autre'}
                        onChange={() =>
                          setCurrentTache({
                            ...currentTache,
                            origineRucher: 'autre',
                          })
                        }
                        className="border-slate-300 text-honey-600 focus:ring-honey-500"
                      />
                      Autre rucher
                    </label>
                    {currentTache.origineRucher === 'autre' && (
                      <div className="pl-6 pt-1">
                        <label htmlFor="autre-rucher-precision" className="sr-only">
                          Précisez le rucher
                        </label>
                        <input
                          id="autre-rucher-precision"
                          type="text"
                          value={currentTache.autreRucherPrecision || ''}
                          onChange={(e) =>
                            setCurrentTache({
                              ...currentTache,
                              autreRucherPrecision: e.target.value,
                            })
                          }
                          placeholder="Précisez le rucher concerné"
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 focus:border-honey-500 text-sm"
                          required
                        />
                      </div>
                    )}
                  </fieldset>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Horaire d&apos;arrivée prévu
                    </label>
                    {heuresPlageModal.length === 0 && (
                      <p className="text-sm text-honey-800 bg-honey-50 border border-honey-200 rounded-lg px-3 py-2 mb-2">
                        Aucun quart d&apos;heure ne correspond à cette plage. Modifiez la plage
                        d&apos;ouverture ou contactez le responsable.
                      </p>
                    )}
                    <div className="flex items-center gap-2">
                      <select
                        value={parseHeureQuarts(reservationForm.horaireArriveePrevue).h}
                        onChange={(e) => {
                          const newH = e.target.value;
                          const allowedM = getMinutesAutoriseesPourHeure(selectedSlot, newH);
                          const curM = parseHeureQuarts(reservationForm.horaireArriveePrevue).m;
                          const nextM = allowedM.includes(curM) ? curM : allowedM[0] || '00';
                          setReservationForm({
                            ...reservationForm,
                            horaireArriveePrevue: joinHeure(newH, nextM),
                          });
                        }}
                        className="flex-1 min-w-0 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 focus:border-honey-500"
                        required
                        disabled={heuresPlageModal.length === 0}
                      >
                        {heuresPlageModal.map((hh) => (
                          <option key={hh} value={hh}>
                            {hh}
                          </option>
                        ))}
                      </select>
                      <span className="text-slate-500">:</span>
                      <select
                        value={parseHeureQuarts(reservationForm.horaireArriveePrevue).m}
                        onChange={(e) =>
                          setReservationForm({
                            ...reservationForm,
                            horaireArriveePrevue: joinHeure(
                              parseHeureQuarts(reservationForm.horaireArriveePrevue).h,
                              e.target.value
                            ),
                          })
                        }
                        className="flex-1 min-w-0 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 focus:border-honey-500"
                        required
                        disabled={heuresPlageModal.length === 0 || minutesPlageModal.length === 0}
                      >
                        {minutesPlageModal.map((mm) => (
                          <option key={mm} value={mm}>
                            {mm}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {reservationModalStep === 5 && (
                <div className="space-y-5">
                  <div className="rounded-xl border border-honey-200 bg-gradient-to-br from-honey-50 via-white to-honey-50/40 px-4 py-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-honey-900/75">
                      Montant estimé (quantité prévue)
                    </p>
                    <p className="mt-1 text-3xl font-bold text-slate-900 tabular-nums">
                      {Number(montantEstime || 0).toFixed(2)}{' '}
                      <span className="text-lg font-semibold text-slate-600">€</span>
                    </p>
                    {soldeAvoir > 0 && (
                      <div className="mt-3 pt-3 border-t border-honey-200/80 text-sm text-slate-700 space-y-1">
                        <p>
                        Votre avoir disponible :{' '}
                          <span className="font-semibold tabular-nums">
                            {Number(soldeAvoir).toFixed(2)} €
                          </span>
                        </p>
                        {choixPaiement === 'immediat' && (
                          <>
                            <p>
                            Votre avoir utilisé :{' '}
                              <span className="font-semibold tabular-nums">
                                {Number(montantAvoirApplique).toFixed(2)} €
                              </span>
                            </p>
                            <p>
                              Reste à payer par carte :{' '}
                              <span className="font-semibold tabular-nums">
                                {Number(montantRestantStripe).toFixed(2)} €
                              </span>
                            </p>
                          </>
                        )}
                      </div>
                    )}
                    <p className="mt-2 text-sm text-slate-600">
                      Le montant définitif sera ajusté lors de la confirmation des quantités
                      réelles par le responsable (dette ou avoir éventuel sur votre solde).
                    </p>
                  </div>

                  <div className="space-y-3">
                    <p className="text-sm font-medium text-slate-900">Mode de règlement</p>
                    <label
                      className={`flex gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                        choixPaiement === 'immediat'
                          ? 'border-honey-500 bg-honey-50 ring-2 ring-honey-400'
                          : 'border-slate-200 hover:border-honey-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="choix-paiement-resa"
                        className="mt-1 text-honey-600"
                        checked={choixPaiement === 'immediat'}
                        onChange={() => setChoixPaiement('immediat')}
                        disabled={reservationSubmitting}
                      />
                      <span className="min-w-0">
                        <span className="flex items-center gap-2 font-medium text-slate-900">
                          <CreditCardIcon className="h-5 w-5 text-honey-700" />
                          Payer maintenant
                        </span>
                        <span className="block text-sm text-slate-600 mt-1">
                          {couvertParAvoir
                            ? 'Votre avoir couvre le montant : confirmation de l’utilisation du solde à l’étape suivante (pas de paiement carte).'
                            : montantAvoirApplique > 0
                              ? `Avoir déduit d’abord, puis paiement carte du reste (${Number(montantRestantStripe).toFixed(2)} €) via Stripe AMAIR.`
                              : 'Paiement sécurisé par carte (Stripe AMAIR).'}
                        </span>
                        <span className="block text-sm text-honey-900 mt-2 bg-honey-100/80 rounded-md px-2 py-1.5">
                          En cas d&apos;annulation, la somme réglée sera portée en crédit sur
                          votre solde miellerie (pas de remboursement sur la carte).
                        </span>
                      </span>
                    </label>
                    <label
                      className={`flex gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                        choixPaiement === 'ulterieur'
                          ? 'border-honey-500 bg-honey-50 ring-2 ring-honey-400'
                          : 'border-slate-200 hover:border-honey-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="choix-paiement-resa"
                        className="mt-1 text-honey-600"
                        checked={choixPaiement === 'ulterieur'}
                        onChange={() => setChoixPaiement('ulterieur')}
                        disabled={reservationSubmitting}
                      />
                      <span className="min-w-0">
                        <span className="font-medium text-slate-900">Régler ultérieurement</span>
                        <span className="block text-sm text-slate-600 mt-1">
                          Le règlement se fera à la confirmation par le responsable.
                        </span>
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {reservationModalStep === 6 && (
                <div className="space-y-4">
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4">
                    <p className="text-sm font-semibold text-emerald-950">
                      Votre avoir couvre l&apos;intégralité du montant
                    </p>
                    <p className="mt-2 text-sm text-emerald-900">
                      Montant de la réservation :{' '}
                      <span className="font-semibold tabular-nums">
                        {Number(montantEstime || 0).toFixed(2)} €
                      </span>
                    </p>
                    <p className="mt-1 text-sm text-emerald-900">
                    Votre avoir disponible :{' '}
                      <span className="font-semibold tabular-nums">
                        {Number(soldeAvoir).toFixed(2)} €
                      </span>
                    </p>
                    <p className="mt-3 text-sm text-emerald-950">
                      Aucun paiement par carte n&apos;est nécessaire. En confirmant,{' '}
                      <strong>{Number(montantEstime || 0).toFixed(2)} €</strong> seront
                      débités de votre solde miellerie.
                    </p>
                  </div>
                  <p className="text-sm text-slate-600">
                    En cas d&apos;annulation, ce montant sera re-crédité sur votre solde (pas
                    de remboursement Stripe).
                  </p>
                </div>
              )}

              <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={closeReservationModal}
                  disabled={reservationSubmitting || estimerLoading}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  Fermer
                </button>
                {reservationModalStep > 1 && (
                  <button
                    type="button"
                    onClick={reculerEtapeReservation}
                    disabled={reservationSubmitting || estimerLoading}
                    className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                  >
                    Précédent
                  </button>
                )}
                {reservationModalStep < 4 && (
                  <button
                    type="button"
                    onClick={avancerEtapeReservation}
                    className="flex-1 min-w-[10rem] px-4 py-2 bg-honey-600 text-white rounded-lg hover:bg-honey-700 transition-colors"
                  >
                    Suivant
                  </button>
                )}
                {reservationModalStep === 4 && (
                  <button
                    type="button"
                    onClick={allerVersEtapePaiementOuConfirmer}
                    disabled={
                      estimerLoading ||
                      reservationSubmitting ||
                      !currentTache.type ||
                      !selectedSlot ||
                      heuresPlageModal.length === 0 ||
                      minutesPlageModal.length === 0
                    }
                    className="flex-1 min-w-[10rem] px-4 py-2 bg-honey-600 text-white rounded-lg hover:bg-honey-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
                  >
                    {estimerLoading ? 'Calcul…' : 'Suivant'}
                  </button>
                )}
                {reservationModalStep === 5 && (
                  <button
                    type="submit"
                    disabled={reservationSubmitting}
                    className="flex-1 min-w-[10rem] px-4 py-2 bg-honey-600 text-white rounded-lg hover:bg-honey-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
                  >
                    {reservationSubmitting
                      ? choixPaiement === 'immediat'
                        ? 'Redirection…'
                        : 'Création…'
                      : choixPaiement === 'immediat'
                        ? couvertParAvoir
                          ? 'Continuer'
                          : montantRestantStripe > 0 && montantAvoirApplique > 0
                            ? `Payer ${Number(montantRestantStripe).toFixed(2)} €`
                            : 'Payer et confirmer'
                        : 'Confirmer la réservation'}
                  </button>
                )}
                {reservationModalStep === 6 && (
                  <button
                    type="submit"
                    disabled={reservationSubmitting}
                    className="flex-1 min-w-[10rem] px-4 py-2 bg-honey-600 text-white rounded-lg hover:bg-honey-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
                  >
                    {reservationSubmitting
                      ? 'Traitement…'
                      : 'Confirmer l’utilisation de mon avoir'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Popup Aide : contact du responsable du créneau */}
      {aideResponsableTarget &&
        (() => {
          const plageA = plageDepuisReservation(aideResponsableTarget);
          return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[58] p-4">
              <div
                className="bg-white rounded-xl shadow-xl max-w-md w-full"
                role="dialog"
                aria-labelledby="aide-responsable-title"
                aria-modal="true"
              >
                <div className="px-6 py-4 border-b border-slate-200">
                  <h3
                    id="aide-responsable-title"
                    className="text-lg font-semibold text-slate-900 flex items-center gap-2"
                  >
                    <QuestionMarkCircleIcon className="h-6 w-6 text-honey-600 shrink-0" />
                    Contacter le responsable
                  </h3>
                  {plageA && (
                    <p className="text-sm text-slate-600 mt-1">
                      Créneau du {dateLocalePlage(plageA)} — {debutPlage(plageA)} à {finPlage(plageA)}
                    </p>
                  )}
                </div>
                <div className="p-6 space-y-4">
                  {!plageA?.responsable ? (
                    <p className="text-sm text-slate-600">
                      Aucune fiche responsable n&apos;est associée à ce créneau. Contactez la miellerie via
                      les canaux de l&apos;association.
                    </p>
                  ) : (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
                      <p className="font-medium text-slate-800 mb-2">
                        {plageA.responsable.prenom} {plageA.responsable.nom}
                      </p>
                      <div className="space-y-2 text-slate-600">
                        {plageA.responsable.email && (
                          <p className="flex items-center gap-2">
                            <EnvelopeIcon className="h-4 w-4 shrink-0" />
                            <a
                              href={`mailto:${plageA.responsable.email}`}
                              className="text-honey-700 hover:underline break-all"
                            >
                              {plageA.responsable.email}
                            </a>
                          </p>
                        )}
                        {plageA.responsable.telephone && (
                          <p className="flex items-center gap-2">
                            <PhoneIcon className="h-4 w-4 shrink-0" />
                            <a
                              href={`tel:${String(plageA.responsable.telephone).replace(/\s/g, '')}`}
                              className="text-honey-700 hover:underline"
                            >
                              {plageA.responsable.telephone}
                            </a>
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={closeAideResponsable}
                    className="w-full px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Modal annulation : > 48 h = motif + confirmation + e-mail responsable ; ≤ 48 h = contacter le responsable */}
      {showAnnulationModal &&
        reservationAnnulationTarget &&
        (() => {
          const plageR = plageDepuisReservation(reservationAnnulationTarget);
          const prestas =
            reservationAnnulationTarget.prestations ||
            reservationAnnulationTarget.taches ||
            [];
          const t0 = prestas[0];
          const peutAnnulerEnLigne = reservationPeutEtreAnnuleeParUsager(
            reservationAnnulationTarget
          );

          return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
              <div
                className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
                role="dialog"
                aria-labelledby="annulation-reservation-title"
                aria-modal="true"
              >
                <div className="px-6 py-4 border-b border-slate-200">
                  <h3
                    id="annulation-reservation-title"
                    className="text-lg font-semibold text-slate-900"
                  >
                    {peutAnnulerEnLigne
                      ? 'Annuler cette réservation ?'
                      : 'Annulation en ligne impossible'}
                  </h3>
                </div>
                <div className="p-6 space-y-4">
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                      <XCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-red-800 font-medium text-sm">Erreur</p>
                        <p className="text-red-700 text-sm mt-0.5">{error}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setError('')}
                        className="text-red-600 hover:text-red-800 shrink-0"
                        aria-label="Fermer le message"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  <div className="text-sm text-slate-700 space-y-1">
                    <p>
                      <span className="font-medium text-slate-900">Date :</span>{' '}
                      {dateLocalePlage(plageR)}
                    </p>
                    <p>
                      <span className="font-medium text-slate-900">Créneau :</span>{' '}
                      {debutPlage(plageR)} – {finPlage(plageR)}
                    </p>
                    <p>
                      <span className="font-medium text-slate-900">Arrivée prévue :</span>{' '}
                      {reservationAnnulationTarget.horaireArriveePrevue}
                    </p>
                    {t0 && (
                      <p>
                        <span className="font-medium text-slate-900">Prestation :</span>{' '}
                        {libelleTacheType(t0.type)}
                      </p>
                    )}
                    {t0 && (
                      <p>
                        <span className="font-medium text-slate-900">Rucher :</span>{' '}
                        {libelleOrigineRucher(t0)}
                      </p>
                    )}
                  </div>

                  {peutAnnulerEnLigne ? (
                    <>
                      <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                        <p className="font-medium">Notification du responsable</p>
                        <p className="mt-1 text-blue-800">
                          Après confirmation, un e-mail est envoyé automatiquement au responsable de la
                          plage pour l’informer de votre annulation (motif inclus).
                        </p>
                      </div>
                      <div>
                        <label
                          htmlFor="raison-annulation"
                          className="block text-sm font-medium text-slate-700 mb-1"
                        >
                          Motif d&apos;annulation <span className="text-red-600">*</span>
                        </label>
                        <textarea
                          id="raison-annulation"
                          value={raisonAnnulation}
                          onChange={(e) => setRaisonAnnulation(e.target.value)}
                          rows={3}
                          required
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 focus:border-honey-500 text-sm"
                          placeholder="Ex. emploi du temps modifié, motif obligatoire pour confirmer"
                        />
                      </div>
                      <div className="flex gap-3 pt-2">
                        <button
                          type="button"
                          onClick={closeAnnulationModal}
                          disabled={annulationSubmitting}
                          className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
                        >
                          Retour
                        </button>
                        <button
                          type="button"
                          onClick={confirmAnnulationReservation}
                          disabled={annulationSubmitting}
                          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                        >
                          {annulationSubmitting ? 'Annulation…' : 'Confirmer l’annulation'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="rounded-lg border border-honey-200 bg-honey-50 px-3 py-2 text-sm text-honey-950">
                        <p className="font-medium">Moins de 48 h avant la plage</p>
                        <p className="mt-1 text-honey-900">
                          L&apos;annulation en ligne n&apos;est plus possible dans ce délai. Veuillez
                          contacter directement le responsable de cette ouverture pour demander une
                          annulation ou un report.
                        </p>
                      </div>
                      {plageR?.responsable ? (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
                          <p className="font-medium text-slate-800 mb-2">Responsable à contacter</p>
                          <p className="text-slate-700">
                            {plageR.responsable.prenom} {plageR.responsable.nom}
                          </p>
                          <div className="mt-2 space-y-1 text-slate-600">
                            {plageR.responsable.email && (
                              <p className="flex items-center gap-2">
                                <EnvelopeIcon className="h-4 w-4 shrink-0" />
                                <a
                                  href={`mailto:${plageR.responsable.email}`}
                                  className="text-honey-700 hover:underline break-all"
                                >
                                  {plageR.responsable.email}
                                </a>
                              </p>
                            )}
                            {plageR.responsable.telephone && (
                              <p className="flex items-center gap-2">
                                <PhoneIcon className="h-4 w-4 shrink-0" />
                                <a
                                  href={`tel:${String(plageR.responsable.telephone).replace(/\s/g, '')}`}
                                  className="text-honey-700 hover:underline"
                                >
                                  {plageR.responsable.telephone}
                                </a>
                              </p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-slate-600">
                          Les coordonnées du responsable ne sont pas disponibles ici. Contactez la
                          miellerie par les moyens habituels de l&apos;association.
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={closeAnnulationModal}
                        className="w-full px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        Fermer
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
}
